<template>
  <div v-if="!singleViewProjectID" class="space-y-4">
    <section class="app-section">
      <div class="app-toolbar project-toolbar">
        <div class="toolbar-heading">
          <h2 class="m-0 text-xl font-semibold">{{ projectsTitle }}</h2>
          <p class="toolbar-subtitle">Всего проектов: {{ isLoading ? '...' : projects.length }}</p>
        </div>
        <div class="toolbar-actions">
          <div class="view-mode-switch" role="group" aria-label="Вид отображения проектов">
            <button
              class="button !h-8 !px-3 !py-0 text-xs"
              :class="projectViewMode === 'tile' ? '' : 'button-outline'"
              @click="setProjectViewMode('tile')"
            >
              Плитка
            </button>
            <button
              class="button !h-8 !px-3 !py-0 text-xs"
              :class="projectViewMode === 'list' ? '' : 'button-outline'"
              @click="setProjectViewMode('list')"
            >
              Список
            </button>
          </div>
          <ButtonBase color="green" :onClick="() => { showModal = true }">Добавить проект</ButtonBase>
        </div>
      </div>
      <ProjectModal
        v-if="showModal"
        :isVisible="showModal"
        @close="showModal = false"
        @project-added="handleProjectAdded"
        :users="users"
        :current-user="currentUser"
        :can-manage-pin="canManageAll"
      />

      <div v-if="isLoading" class="state-box">
        <p>Загрузка проектов...</p>
      </div>
      <div v-else-if="projects.length === 0" class="state-box">
        <p>Нет доступных проектов</p>
      </div>
      <div v-else-if="projectViewMode === 'tile'" class="projects-grid">
        <ProjectCard
          v-for="project in projects"
          :key="project.id"
          :project="project"
          :showProject="showProject"
        />
      </div>
      <div v-else class="overflow-x-auto">
        <table class="app-table min-w-[1040px]">
          <thead>
            <tr>
              <th>Проект</th>
              <th>Категория</th>
              <th>Дата</th>
              <th>Pin</th>
              <th>Статусы</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="project in projects" :key="project.id" :class="{ 'project-row-hidden': !project.visible }">
              <td class="max-w-[460px]">
                <button class="project-title-button" @click="showProject(project.id)">
                  {{ project.title || 'Без названия' }}
                </button>
                <p class="project-preview">{{ projectPreview(project.content) }}</p>
              </td>
              <td>{{ project.category || '—' }}</td>
              <td>{{ formatProjectDate(project.date) || '—' }}</td>
              <td>{{ project.pin ?? '—' }}</td>
              <td>
                <div class="status-stack">
                  <span class="status-pill" :class="project.visible ? 'status-pill-on' : 'status-pill-off'">
                    Сайт: {{ project.visible ? 'Виден' : 'Скрыт' }}
                  </span>
                  <span class="status-pill" :class="project.visible_shared ? 'status-pill-on' : 'status-pill-off'">
                    Общий: {{ project.visible_shared ? 'Виден' : 'Скрыт' }}
                  </span>
                </div>
              </td>
              <td>
                <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="showProject(project.id)">
                  Открыть
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
  <div v-else>
    <div class="app-section">
      <ProjectSingleView
        :projectID="singleViewProjectID"
        :showProjectList="showProjectList"
        :current-user="currentUser"
        :can-manage-all="canManageAll"
      />
    </div>
  </div>
</template>

<script>
import ProjectModal from './ProjectModal.vue';
import ProjectCard from './ProjectCard.vue';
import { fetchProjects, fetchMyProjects } from '../services/api';
import ProjectSingleView from './ProjectSingleView.vue';
import ButtonBase from './ButtonBase.vue';
import { isUserAdminForUi } from '../utils/user-role-override';

const PROJECT_VIEW_MODE_STORAGE_KEY = 'chapter.admin.projects.view_mode';

const readProjectViewMode = () => {
  if (typeof window === 'undefined') return 'tile';
  try {
    const value = window.localStorage.getItem(PROJECT_VIEW_MODE_STORAGE_KEY);
    return value === 'list' ? 'list' : 'tile';
  } catch (error) {
    return 'tile';
  }
};

const writeProjectViewMode = (value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PROJECT_VIEW_MODE_STORAGE_KEY, value);
  } catch (error) {
    // Игнорируем ошибку доступа к localStorage
  }
};

export default {
  name: 'ProjectView',
  props: {
    currentUser: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    ProjectModal,
    ProjectCard,
    ProjectSingleView,
    ButtonBase
  },
  computed: {
    canManageAll() {
      return Boolean(this.currentUser?.is_superuser) || isUserAdminForUi(this.currentUser);
    },
    projectsTitle() {
      return this.canManageAll ? 'Проекты' : 'Мои проекты';
    }
  },
  data() {
    return {
      showModal: false,
      showEditModal: false,
      showDeleteModal: false,
      selectedProject: null,
      users: [],
      projects: [],
      singleViewProjectID: null,
      isLoading: false,
      projectViewMode: readProjectViewMode(),
    };
  },
  watch: {
    currentUser: {
      deep: true,
      handler() {
        this.loadProjects();
      }
    }
  },
  mounted() {
    this.loadProjects();
  },
  methods: {
    setProjectViewMode(mode) {
      const normalizedMode = mode === 'list' ? 'list' : 'tile';
      this.projectViewMode = normalizedMode;
      writeProjectViewMode(normalizedMode);
    },
    formatProjectDate(date) {
      if (!date) return '';
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toLocaleDateString('ru-RU');
    },
    projectPreview(content) {
      const text = String(content || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!text) return 'Без описания';
      if (text.length <= 160) return text;
      return `${text.slice(0, 160)}...`;
    },
    showProject(projectID) {
      this.singleViewProjectID = projectID;
    },
    showProjectList() {
      this.singleViewProjectID = null;
      this.loadProjects();
    },
    handleProjectAdded() {
      this.showModal = false;
      this.loadProjects();
    },
    async loadProjects() {
      try {
        this.isLoading = true;
        this.projects = []; // Очищаем список перед загрузкой
        const response = this.canManageAll ? await fetchProjects() : await fetchMyProjects();
        if (response && Array.isArray(response)) {
          this.projects = response;
        } else {
          console.error('Данные не являются массивом:', response);
          this.projects = [];
        }
        return this.projects;
      } catch (error) {
        console.error('Ошибка при получении проектов:', error);
        this.projects = [];
      } finally {
        this.isLoading = false;
      }
    },
    addProject(newProject) {
      const newId = this.projects.length + 1;
      this.projects.push({ id: newId, ...newProject, users: [] });
    },

    // Edit flow
    onEditProject(project) {
      this.selectedProject = project;
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.selectedProject = null;
    },
    handleUpdateProject(updatedProject) {
      const idx = this.projects.findIndex(p => p.id === updatedProject.id);
      if (idx !== -1) {
        this.projects.splice(idx, 1, { ...updatedProject });
      }
      this.showEditModal = false;
      this.selectedProject = null;
    },

    // Delete flow
    onPromptDeleteProject(project) {
      this.selectedProject = project;
      this.showDeleteModal = true;
    },
    confirmDeleteProject() {
      if (this.selectedProject && this.selectedProject.id) {
        this.handleDeleteProject(this.selectedProject.id);
      }
      this.showDeleteModal = false;
      this.selectedProject = null;
    },
    handleDeleteProject(id) {
      // Remove from local state
      this.projects = this.projects.filter(p => p.id !== id);
      // This is where you would call the API for deletion!
      this.finalizeProjectDelete(id); // handler stub
    },
  }
}
</script>

<style scoped>
.project-toolbar {
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}

.toolbar-heading {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toolbar-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: hsl(var(--muted-foreground));
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.view-mode-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.projects-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}

.state-box {
  border: 1px solid hsl(var(--border));
  border-radius: 12px;
  background: hsl(var(--muted));
  color: hsl(var(--muted-foreground));
  text-align: center;
  padding: 16px;
  font-size: 0.95rem;
}

.project-row-hidden {
  opacity: 0.75;
}

.project-title-button {
  border: 0;
  background: transparent;
  color: hsl(var(--accent));
  font-size: 0.9rem;
  font-weight: 700;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: pointer;
}

.project-title-button:hover {
  text-decoration: underline;
}

.project-preview {
  margin: 4px 0 0;
  font-size: 0.8rem;
  line-height: 1.35;
  color: hsl(var(--muted-foreground));
}

.status-stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
}

.status-pill-on {
  background: hsl(var(--success) / 0.16);
  color: hsl(var(--success));
}

.status-pill-off {
  background: hsl(var(--destructive) / 0.12);
  color: hsl(var(--destructive));
}

@media (max-width: 720px) {
  .projects-grid {
    grid-template-columns: 1fr;
  }
}
</style>
